import { appendCsrfToUrlSearchParams, getCsrfToken } from '~/composables/useCsrfToken';

/** JSON/역직렬화로 필드가 문자열 "null" / "undefined" 인 경우까지 빈값으로 통일 (엑셀 다운로드 POST용) */
const NULLISH_STRINGS = new Set(['null', 'undefined']);

const normalizeExcelFormValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (NULLISH_STRINGS.has(s.trim().toLowerCase())) return '';
  return s;
};

const formRecordToUrlSearchParams = (record: Record<string, unknown>): URLSearchParams => {
  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(record)) {
    normalized[key] = normalizeExcelFormValue(value);
  }
  return new URLSearchParams(normalized);
};

/** `URLSearchParams.append(key, null)` 등은 본문에 리터럴 "null" 이 들어가므로 동일 규칙으로 재구성 */
const cloneUrlSearchParamsNormalized = (input: URLSearchParams): URLSearchParams => {
  const out = new URLSearchParams();
  input.forEach((value, key) => {
    out.append(key, normalizeExcelFormValue(value));
  });
  return out;
};

export const useExcelDownload = () => {
  const downloadExcel = async ({
    actionUrl,
    formData,
    downloadFileName
  }: {
    actionUrl: string;
    formData: Record<string, any> | URLSearchParams;
    downloadFileName?: string;
  }) => {
    try {
      const config = useRuntimeConfig();
      const params =
        formData instanceof URLSearchParams
          ? cloneUrlSearchParamsNormalized(formData)
          : formRecordToUrlSearchParams(formData);
      appendCsrfToUrlSearchParams(params);

      // 환경별 URL 처리: 빌드환경에서는 /api 제거
      const processedUrl = config.public.isDev
        ? actionUrl // 개발환경: 그대로 사용 (프록시가 처리)
        : actionUrl.replace(/^\/api\//, '/'); // 빌드환경: /api/ 제거

      const csrf = getCsrfToken();
      const res = await fetch(`${config.public.apiBase}${processedUrl}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,*/*',
          ...(csrf ? { 'X-XSRF-TOKEN': csrf } : {})
        },
        body: params.toString()
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Excel download failed: ${res.status} - ${errorText}`);
      }

      // Content-Type 확인
      const contentType = res.headers.get('Content-Type');
      if (
        !contentType ||
        (!contentType.includes('excel') &&
          !contentType.includes('spreadsheet') &&
          !contentType.includes('octet-stream'))
      ) {
        console.warn('Unexpected content type:', contentType);
      }

      // blob 받기
      const blob = await res.blob();

      // blob 크기 확인
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // 서버에서 내려준 파일명(Content-Disposition 헤더에 destJxlsFileName 세팅됨)
      const contentDisposition = res.headers.get('Content-Disposition');
      let filename = 'download.xls';

      if (contentDisposition) {
        // 다양한 Content-Disposition 형식 지원
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // URL 인코딩된 파일명 디코딩
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            console.warn('Failed to decode filename:', filename);
          }
        }
      }

      // 파일 확장자 확인 및 추가
      if (!filename.includes('.')) {
        filename += '.xls';
      }

      // 다운로드 처리
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFileName || filename;
      a.style.display = 'none';

      // DOM에 추가 후 클릭
      document.body.appendChild(a);
      a.click();

      // 정리
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log(`Excel file downloaded successfully: ${filename}`);
    } catch (error) {
      console.error('Excel download error:', error);
      throw error;
    }
  };

  return { downloadExcel };
};
